import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Spinner } from '@/components/ui/Spinner';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');

  useEffect(() => {
    const handle = async () => {
      // Short sign-in links (coach worker /go/{code}) arrive with a token hash
      // instead of the Supabase verify redirect: redeem it here. Only this
      // page's JavaScript spends the one-time token, so inbox link scanners
      // that pre-open URLs can't burn it.
      const query = new URLSearchParams(window.location.search);
      const tokenHash = query.get('token_hash');
      const type = query.get('type');
      if (tokenHash && (type === 'magiclink' || type === 'email' || type === 'signup')) {
        window.history.replaceState(null, '', window.location.pathname);
        const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
        if (!error) {
          setStatus('success');
          setTimeout(() => navigate('/coach', { replace: true }), 1500);
          return;
        }
        // Already used (e.g. a second tab), but this browser may already be signed in.
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          navigate('/coach', { replace: true });
          return;
        }
        setStatus('error');
        return;
      }

      // Supabase with detectSessionInUrl:true handles the hash automatically.
      // Wait briefly for the SIGNED_IN event to fire, then check session.
      await new Promise((r) => setTimeout(r, 800));
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setStatus('success');
        setTimeout(() => navigate('/coach', { replace: true }), 1500);
      } else {
        // Try parsing the hash manually (fallback)
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const access_token = params.get('access_token');
        const refresh_token = params.get('refresh_token');
        if (access_token && refresh_token) {
          const { error } = await supabase.auth.setSession({ access_token, refresh_token });
          if (!error) {
            setStatus('success');
            setTimeout(() => navigate('/coach', { replace: true }), 1500);
            return;
          }
        }
        setStatus('error');
      }
    };
    handle();
  }, []);

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: '#050507',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '16px',
      padding: '32px',
    }}>
      {status === 'processing' && (
        <>
          <Spinner size="lg" />
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)' }}>Signing you in…</p>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircle size={56} color="#22C55E" />
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#fff', margin: 0 }}>You’re signed in</h1>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', textAlign: 'center' }}>
            Taking you to your Coach…
          </p>
        </>
      )}

      {status === 'error' && (
        <>
          <p style={{ fontSize: '16px', color: '#EF4444', textAlign: 'center' }}>
            This sign-in link is invalid or has already been used. Request a new one.
          </p>
          <button
            onClick={() => navigate('/auth', { replace: true })}
            style={{
              marginTop: '8px', backgroundColor: '#FB3AAE', color: '#fff',
              border: 'none', borderRadius: '14px', padding: '14px 40px',
              fontSize: '15px', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Sign In
          </button>
        </>
      )}
    </div>
  );
}
